"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SliderTrack = exports.SliderThumb = exports.SliderOutput = exports.SliderLabel = exports.Slider = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_1 = __importDefault(require("react"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../theme");
var Form_1 = require("./Forms/Form");
/**
 * A slider component that allows users to select a value from a range by dragging a thumb along a track.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Slider.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-slider--default}
 *
 * @example
 * ```tsx
 * <Slider
 *   label="Volume"
 *   minValue={0}
 *   maxValue={100}
 *   defaultValue={50}
 *   formatOptions={{ style: 'percent' }}
 * />
 * ```
 *
 * @param formatOptions - The display format of the value label
 * @param orientation - The orientation of the Slider. Defaults to 'horizontal'
 * @param isDisabled - Whether the whole Slider is disabled
 * @param minValue - The slider's minimum value. Defaults to 0
 * @param maxValue - The slider's maximum value. Defaults to 100
 * @param step - The slider's step value. Defaults to 1
 * @param value - The current value (controlled)
 * @param defaultValue - The default value (uncontrolled)
 * @param children - The children of the component. Can be ReactNode or a render function
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 * @param onChangeEnd - Fired when the slider stops moving, due to being let go
 * @param onChange - Handler that is called when the value changes
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.Slider = (0, styled_1.default)(react_aria_components_1.Slider)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: grid;\n  grid-template-areas:\n    'label output'\n    'track track';\n  grid-template-columns: 1fr auto;\n  max-width: 300px;\n  color: ", ";\n"], ["\n  display: grid;\n  grid-template-areas:\n    'label output'\n    'track track';\n  grid-template-columns: 1fr auto;\n  max-width: 300px;\n  color: ", ";\n"])), theme_1.theme.colors.n900);
/**
 * A label for the slider that describes its purpose.
 *
 * @example
 * ```tsx
 * <SliderLabel>Volume</SliderLabel>
 * ```
 *
 * @param children - The content to display in the label
 */
exports.SliderLabel = (0, styled_1.default)(Form_1.Label)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  grid-area: label;\n"], ["\n  grid-area: label;\n"])));
/**
 * An output element that displays the current value of the slider.
 *
 * @example
 * ```tsx
 * <SliderOutput />
 * ```
 */
exports.SliderOutput = (0, styled_1.default)(react_aria_components_1.SliderOutput)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  grid-area: output;\n"], ["\n  grid-area: output;\n"])));
/**
 * A thumb that can be dragged along the track to change the slider's value.
 * Provides visual feedback for different states (hover, focus, dragging).
 *
 * @example
 * ```tsx
 * <SliderThumb />
 * ```
 */
exports.SliderThumb = (0, styled_1.default)(react_aria_components_1.SliderThumb)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  width: ", ";\n  height: ", ";\n  border-radius: ", ";\n  background: ", ";\n  border: ", " solid ", ";\n  forced-color-adjust: none;\n\n  &[data-hovered] {\n    background: ", ";\n  }\n\n  &[data-dragging] {\n    background: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n  }\n  top: 50%;\n"], ["\n  width: ", ";\n  height: ", ";\n  border-radius: ", ";\n  background: ", ";\n  border: ", " solid ", ";\n  forced-color-adjust: none;\n\n  &[data-hovered] {\n    background: ", ";\n  }\n\n  &[data-dragging] {\n    background: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n  }\n  top: 50%;\n"])), theme_1.theme.space.space20, theme_1.theme.space.space20, theme_1.theme.radii.circle, theme_1.theme.colors.actionPrimary, theme_1.theme.borderWidths.thick, theme_1.theme.colors.white, theme_1.theme.colors.actionPrimaryHovered, theme_1.theme.colors.actionPrimaryPressed, theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400);
var StyledSliderTrack = (0, styled_1.default)(react_aria_components_1.SliderTrack)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  grid-area: track;\n  position: relative;\n  height: 30px;\n  width: 100%;\n"], ["\n  grid-area: track;\n  position: relative;\n  height: 30px;\n  width: 100%;\n"])));
var Track = styled_1.default.div(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  display: block;\n  position: absolute;\n  background: ", ";\n  border-radius: ", ";\n  height: 3px;\n  width: 100%;\n  top: 50%;\n  transform: translateY(-50%);\n  border-radius: ", ";\n"], ["\n  display: block;\n  position: absolute;\n  background: ", ";\n  border-radius: ", ";\n  height: 3px;\n  width: 100%;\n  top: 50%;\n  transform: translateY(-50%);\n  border-radius: ", ";\n"])), theme_1.theme.colors.n300, theme_1.theme.radii.circle, theme_1.theme.radii.circle);
var Fill = styled_1.default.div(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  display: block;\n  position: absolute;\n  height: 3px;\n  background: ", ";\n  width: ", "%;\n  top: 50%;\n  transform: translateY(-50%);\n  border-radius: ", ";\n"], ["\n  display: block;\n  position: absolute;\n  height: 3px;\n  background: ", ";\n  width: ", "%;\n  top: 50%;\n  transform: translateY(-50%);\n  border-radius: ", ";\n"])), theme_1.theme.colors.blue500, function (props) { return props.width * 100; }, theme_1.theme.radii.circle);
/**
 * A track that the slider thumb moves along, showing the range of available values.
 * Includes a background track and a filled portion showing the current value.
 *
 * @example
 * ```tsx
 * <SliderTrack>
 *   <SliderFill />
 *   <SliderThumb />
 * </SliderTrack>
 * ```
 *
 * @param children - The content to display in the track
 */
var SliderTrack = function (props) { return (react_1.default.createElement(StyledSliderTrack, __assign({}, props), function (_a) {
    var state = _a.state;
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(Track, null),
        react_1.default.createElement(Fill, { width: state.getThumbPercent(0) }),
        props.children));
})); };
exports.SliderTrack = SliderTrack;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
//# sourceMappingURL=Slider.js.map